import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { fetchMagicSignAuditLog, cancelMagicSign, updateMagicSignConfig, downloadMagicSignDoc } from './MagicSignService';
import { MagicSignRow, MagicSignStatus, SignType, Signatory, AuditLogEvent } from './types';

interface CommonHandlerDeps<TRow extends MagicSignRow> {
    setRows: React.Dispatch<React.SetStateAction<TRow[]>>;
    setAuditLogRowName: React.Dispatch<React.SetStateAction<string>>;
    setAuditLogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setAuditLogLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setAuditLogEvents: React.Dispatch<React.SetStateAction<AuditLogEvent[]>>;
    setCancelConfirmId: React.Dispatch<React.SetStateAction<string | null>>;
    setDocIdToView: React.Dispatch<React.SetStateAction<string>>;
    setDocNameToView: React.Dispatch<React.SetStateAction<string>>;
    setActiveRowId: React.Dispatch<React.SetStateAction<string | null>>;
    fetchList: (page?: number) => Promise<void>;
    /**
     * Called after a successful config update with the optimistically-merged row. The parent decides
     * whether the row is now complete (Doc Type + Sign Type + Signatory) and, if so, auto-generates
     * the signing links. Optional — views that don't auto-generate can omit it.
     */
    onMaybeAutoGenerate?: (row: TRow) => void;
}

/**
 * Shared action-handler factory for both the company-scoped and global Magic Sign views.
 * Pattern reference: useMagicSignLogic.tsx (company view), useGlobalMagicSignLogic.ts (global view).
 */
const useMagicSignCommonHandlers = <TRow extends MagicSignRow>({
    setRows,
    setAuditLogRowName,
    setAuditLogOpen,
    setAuditLogLoading,
    setAuditLogEvents,
    setCancelConfirmId,
    setDocIdToView,
    setDocNameToView,
    setActiveRowId,
    fetchList,
    onMaybeAutoGenerate,
}: CommonHandlerDeps<TRow>) => {
    // Applies a field change to the matching row, captures the merged row, and (on update success)
    // hands it to the parent so it can decide whether to auto-generate the signing links.
    const applyConfigUpdate = useCallback(
        async (id: string, patch: Partial<TRow>, payload: Parameters<typeof updateMagicSignConfig>[0], errorMsg: string) => {
            let mergedRow: TRow | undefined;
            setRows((prev) =>
                prev.map((r) => {
                    if (r.id !== id) return r;
                    mergedRow = { ...r, ...patch };
                    return mergedRow;
                }),
            );
            try {
                await updateMagicSignConfig(payload);
                if (mergedRow) onMaybeAutoGenerate?.(mergedRow);
            } catch (error) {
                console.error('[MagicSign] Failed to update config', error);
                toast.error(error instanceof Error && error.message ? error.message : errorMsg);
                fetchList(0);
            }
        },
        [setRows, fetchList, onMaybeAutoGenerate],
    );

    const handleUpdateDocType = useCallback(
        (id: string, documentType: string) =>
            applyConfigUpdate(id, { documentType } as Partial<TRow>, { id, documentType }, 'Failed to update document type.'),
        [applyConfigUpdate],
    );

    const handleUpdateSignType = useCallback(
        (id: string, signType: SignType) =>
            applyConfigUpdate(id, { signType } as Partial<TRow>, { id, signType }, 'Failed to update sign type.'),
        [applyConfigUpdate],
    );

    const handleUpdateSignatories = useCallback(
        (id: string, signatories: Signatory[]) =>
            applyConfigUpdate(id, { signatories } as Partial<TRow>, { id, signatories }, 'Failed to update signatories.'),
        [applyConfigUpdate],
    );

    const handleCancelConfirm = useCallback(
        async (id: string) => {
            setCancelConfirmId(null);
            setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: MagicSignStatus.CANCELLED } : r)));
            try {
                await cancelMagicSign({ id });
                toast.success('Signing link cancelled.');
            } catch (error) {
                console.error('[MagicSign] Failed to cancel signing', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to cancel signing.');
                fetchList(0);
            }
        },
        [setRows, setCancelConfirmId, fetchList],
    );

    const handleViewAuditLog = useCallback(
        async (row: MagicSignRow) => {
            setAuditLogRowName(row.fileName);
            setAuditLogOpen(true);
            setAuditLogLoading(true);
            setAuditLogEvents([]);
            try {
                const res = await fetchMagicSignAuditLog(row.id);
                const events: AuditLogEvent[] = Array.isArray(res?.data?.data) ? res.data.data : [];
                // Hide events that aren't part of the per-signatory signing trail in the drawer:
                //  - "Associated Company" (org assignment, not a signing action)
                //  - "Completed" (the overall completion roll-up — the row status already shows
                //    All Signed / Partial Signed; the drawer lists individual signatory actions).
                setAuditLogEvents(
                    events.filter((e) => {
                        const ev = e.event ?? '';
                        return !/^Associated Company/i.test(ev) && !/^Completed/i.test(ev);
                    }),
                );
            } catch (error) {
                console.error('[MagicSign] Failed to load audit log', error);
                toast.error('Failed to load audit log.');
            } finally {
                setAuditLogLoading(false);
            }
        },
        [setAuditLogRowName, setAuditLogOpen, setAuditLogLoading, setAuditLogEvents],
    );

    const handleDownloadDocument = useCallback(async (row: MagicSignRow) => {
        // Use the backend download endpoint — it is version-aware (returns the signed copy when
        // the doc is signed) and resolves the correct org (the signed PDF is stored under the
        // company org, not the PNO), unlike the direct-to-Mnemosyne helper which assumed the PNO.
        try {
            const res = await downloadMagicSignDoc(row.id);
            if (!res?.data) {
                toast.error('Failed to download document.');
                return;
            }
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = row.fileName ?? 'document.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('[MagicSign] Failed to download document', error);
            toast.error('Failed to download document.');
        }
    }, []);

    const handleCopySigningLinks = useCallback((row: MagicSignRow) => {
        if (!row.signingLinks?.length) {
            toast.info('Signing links not available yet.');
            return;
        }
        const text = row.signingLinks
            .map((sl, idx) => `Signatory ${idx + 1} Signing Link${sl.orgName ? ` (${sl.orgName})` : ''}: ${sl.link}`)
            .join('\n');
        navigator.clipboard
            .writeText(text)
            .then(() => toast.success('Signing links copied to clipboard.'))
            .catch((error) => {
                console.error('[MagicSign] Failed to copy signing links', error);
                toast.error('Failed to copy links.');
            });
    }, []);

    const handleRowClick = useCallback(
        (row: MagicSignRow) => {
            if (row.status === MagicSignStatus.UPLOADING) return;
            setActiveRowId(row.id);
            const viewId = row.signedMnemosyneDocId ?? row.signedDocId ?? row.mnemosyneDocId ?? '';
            setDocIdToView(viewId);
            setDocNameToView(row.fileName);
        },
        [setActiveRowId, setDocIdToView, setDocNameToView],
    );

    return {
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
    };
};

export default useMagicSignCommonHandlers;
